/**
 * Single source of truth for scrubbing `undefined` out of a payload destined
 * for Firestore.
 *
 * The frontend Firestore SDK is initialised WITHOUT `ignoreUndefinedProperties`
 * (unlike the backend Admin SDK), so it rejects the WHOLE write — `setDoc`,
 * `updateDoc`, `addDoc`, `arrayUnion(...)` — as soon as a single `undefined`
 * value appears, **including `undefined` nested inside an object that itself
 * sits inside an array** (e.g. a `Contact` inside `tenant.contacts[]`, an owner
 * inside `owners[]`). When that happens the action silently fails: the
 * optimistic UI shows the change, the write throws, the cache rolls back, and
 * the user is left believing the data was saved.
 *
 * `deepStripUndefined` recursively removes those values:
 *  - Arrays: each element is cleaned; `undefined`/function elements are dropped
 *    (Firestore can't store them anyway).
 *  - Objects (plain or class instances): own enumerable keys whose value is
 *    `undefined` or a function are dropped, the rest are cleaned recursively.
 *    Empty objects are PRESERVED — `{}` can signal feature presence
 *    (e.g. `garageDetails: {}` ⇒ "has a garage, no details yet").
 *  - `null`, primitives, `Date`, and Firestore `Timestamp` (anything exposing a
 *    `toDate()` method) are returned untouched.
 *
 * It deliberately does NOT special-case the other FieldValue sentinels
 * (`arrayUnion`, `deleteField`, `serverTimestamp`): those are applied by callers
 * to the payload and must never be passed THROUGH this function.
 */
export function deepStripUndefined<T>(value: T): T {
  return cleanValue(value) as T;
}

function cleanValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value
      .filter(item => item !== undefined && typeof item !== 'function')
      .map(item => cleanValue(item));
  }

  if (value instanceof Date) {
    return value;
  }

  if (value && typeof value === 'object') {
    // Firestore Timestamp (or any `{ toDate() }` sentinel) — leave intact,
    // never recurse into it.
    if (typeof (value as { toDate?: unknown }).toDate === 'function') {
      return value;
    }

    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      if (val === undefined || typeof val === 'function') continue;
      out[key] = cleanValue(val);
    }
    return out;
  }

  // null, string, number, boolean, bigint, symbol
  return value;
}
